import { LeadList } from "../../../types/neighborhoodSearch";
import { SQFT_MAX, ACREAGE_MAX, BEDS_MAX, BATHS_MAX } from "../../../constants/neighborhoodSearch";
import { parsePropertyTypes } from "../constants";
import { getYearBuiltMax } from "../../../utils/neighborhoodSearch";

interface RangeFilter {
  min: number;
  max: number;
}

/**
 * Transform saved search data to normalize filters.
 * - Converts concatenated property types (e.g., "SCT") to comma-delimited format ("S,C,T").
 * - Caps squareFootage max to SQFT_MAX.
 * - Caps acreage max to ACREAGE_MAX.
 * - Caps bedrooms max to BEDS_MAX.
 * - Caps bathrooms max to BATHS_MAX.
 * - Caps yearBuilt max to current year.
 */
export const transformSavedSearch = <T extends LeadList>(savedSearch: T): T => {
  const searchParams = savedSearch?.search_parameters as Record<string, unknown> | undefined;
  const searchFilters = searchParams?.searchFilters as Record<string, unknown> | undefined;

  const updatedFilters = { ...searchFilters };
  let hasChanges = false;

  // Normalize property types
  const propertyType = searchFilters?.propertyType as string | undefined;
  if (propertyType) {
    const propertyTypes = parsePropertyTypes(propertyType);
    updatedFilters.propertyType = propertyTypes.join(',');
    hasChanges = true;
  }

  // Cap squareFootage max to SQFT_MAX
  const squareFootage = searchFilters?.squareFootage as RangeFilter | undefined;
  if (squareFootage && typeof squareFootage === 'object' && 'max' in squareFootage && squareFootage.max > SQFT_MAX) {
    updatedFilters.squareFootage = { ...squareFootage, max: SQFT_MAX };
    hasChanges = true;
  }

  // Cap acreage max to ACREAGE_MAX
  const acreage = searchFilters?.acreage as RangeFilter | undefined;
  if (acreage && typeof acreage === 'object' && 'max' in acreage && acreage.max > ACREAGE_MAX) {
    updatedFilters.acreage = { ...acreage, max: ACREAGE_MAX };
    hasChanges = true;
  }

  // Cap bedrooms max to BEDS_MAX
  const bedrooms = searchFilters?.bedrooms as RangeFilter | undefined;
  if (bedrooms && typeof bedrooms === 'object' && 'max' in bedrooms && bedrooms.max > BEDS_MAX) {
    updatedFilters.bedrooms = { ...bedrooms, max: BEDS_MAX };
    hasChanges = true;
  }

  // Cap bathrooms max to BATHS_MAX
  const bathrooms = searchFilters?.bathrooms as RangeFilter | undefined;
  if (bathrooms && typeof bathrooms === 'object' && 'max' in bathrooms && bathrooms.max > BATHS_MAX) {
    updatedFilters.bathrooms = { ...bathrooms, max: BATHS_MAX };
    hasChanges = true;
  }

  // Cap yearBuilt max to current year
  const yearBuiltMax = getYearBuiltMax();
  const yearBuilt = searchFilters?.yearBuilt as RangeFilter | undefined;
  if (yearBuilt && typeof yearBuilt === 'object' && 'max' in yearBuilt && yearBuilt.max > yearBuiltMax) {
    updatedFilters.yearBuilt = { ...yearBuilt, max: yearBuiltMax };
    hasChanges = true;
  }

  if (hasChanges) {
    return {
      ...savedSearch,
      search_parameters: {
        ...searchParams,
        searchFilters: updatedFilters,
      },
    };
  }
  return savedSearch;
};
